import { useLanguage } from '../hooks/useLanguage';

interface Submission {
  id: number;
  task_id: number;
  task_title?: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

interface ProgressCardProps {
  balanceUsd: number; // in cents
  totalEarnedUsd: number; // in cents
  tasksCompleted: number;
  pendingSubmissions: Submission[];
  minPayout?: number; // in cents, default 500 ($5)
}

export function ProgressCard({
  balanceUsd,
  totalEarnedUsd,
  tasksCompleted,
  pendingSubmissions,
  minPayout = 500,
}: ProgressCardProps) {
  const { language } = useLanguage();
  const isRtl = language === 'ar';

  const balanceDollars = balanceUsd / 100;
  const minPayoutDollars = minPayout / 100;
  const progressPercent = Math.min((balanceUsd / minPayout) * 100, 100);
  const remaining = Math.max(minPayout - balanceUsd, 0) / 100;

  const pendingCount = pendingSubmissions.filter(s => s.status === 'pending').length;

  // Calculate time since oldest pending submission
  const oldestPending = pendingSubmissions
    .filter(s => s.status === 'pending')
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())[0];

  const getTimeAgo = (dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return isRtl ? `${diffDays} يوم` : `${diffDays}d ago`;
    }
    if (diffHours > 0) {
      return isRtl ? `${diffHours} ساعة` : `${diffHours}h ago`;
    }
    return isRtl ? 'الآن' : 'Just now';
  };

  return (
    <div className="space-y-4">
      {/* Payout Progress */}
      <div className="bg-tg rounded-xl p-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-tg">
            {isRtl ? 'تقدم السحب' : 'Payout Progress'}
          </span>
          <span className="text-sm text-tg-hint">
            ${balanceDollars.toFixed(2)} / ${minPayoutDollars.toFixed(2)}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="w-full h-3 bg-tg-secondary rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        <div className="mt-2 text-xs text-tg-hint">
          {balanceUsd >= minPayout ? (
            <span className="text-green-500 font-medium">
              {isRtl ? 'جاهز للسحب!' : 'Ready to withdraw!'}
            </span>
          ) : (
            <span>
              {isRtl
                ? `${remaining.toFixed(2)}$ متبقية للسحب`
                : `$${remaining.toFixed(2)} more to withdraw`}
            </span>
          )}
        </div>
      </div>

      {/* Tasks Stats */}
      <div className="grid grid-cols-3 gap-3">
        {/* Approved */}
        <div className="bg-tg rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-green-500">{tasksCompleted}</div>
          <div className="text-xs text-tg-hint">
            {isRtl ? 'موافق عليها' : 'Approved'}
          </div>
        </div>

        {/* Pending */}
        <div className="bg-tg rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-yellow-500">{pendingCount}</div>
          <div className="text-xs text-tg-hint">
            {isRtl ? 'قيد المراجعة' : 'Pending'}
          </div>
        </div>

        {/* Total Earned */}
        <div className="bg-tg rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-tg">${(totalEarnedUsd / 100).toFixed(2)}</div>
          <div className="text-xs text-tg-hint">
            {isRtl ? 'إجمالي الأرباح' : 'Total Earned'}
          </div>
        </div>
      </div>

      {/* Pending Reviews List */}
      {pendingCount > 0 && (
        <div className="bg-tg rounded-xl p-4">
          <h3 className="text-sm font-medium text-tg mb-3">
            {isRtl ? 'في انتظار المراجعة' : 'Awaiting Review'}
          </h3>
          <div className="space-y-2">
            {pendingSubmissions
              .filter(s => s.status === 'pending')
              .slice(0, 5)
              .map((submission) => (
                <div
                  key={submission.id}
                  className="flex justify-between items-center py-2 border-b border-tg-secondary last:border-0"
                >
                  <span className="text-sm text-tg truncate flex-1">
                    {submission.task_title || `Task #${submission.task_id}`}
                  </span>
                  <span className="text-xs text-tg-hint ml-2 rtl:ml-0 rtl:mr-2">
                    {getTimeAgo(submission.created_at)}
                  </span>
                </div>
              ))}
          </div>
          {oldestPending && (
            <div className="mt-3 text-xs text-tg-hint">
              {isRtl
                ? 'المراجعات عادة تستغرق 24-48 ساعة'
                : 'Reviews typically take 24-48 hours'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
